import { useState, useEffect } from 'react';

// Has the visitor interacted with THIS page yet?
//
// A pure-CSS `:hover` menu does not need a movement to open. The browser
// evaluates :hover against wherever the cursor already is the moment the page
// renders, so following a link OUT of an open dropdown lands on the next page
// with the cursor still sitting where that menu was -- and the menu opens by
// itself, over the page the visitor just asked for. It then sustains itself:
// the cursor is inside the open menu, so the ancestor stays hovered, and it
// will not close until the visitor moves away.
//
// Reproduced on the customer home page: click Order -> School Pictures, and the
// Purchase Pictures card renders with the Order menu still hanging over it.
// Dropping focus did not close it (:focus-within was already false); the
// ancestor still matched :hover.
//
// Focus leaks the same way. Clicking a link inside a dropdown leaves that link
// focused, and the next document can load with focus still on it -- :focus-within
// then opens the menu, the cursor is now inside the open menu, and :hover takes
// over and holds it. Gating only :hover cannot break that chain, so both rules
// are gated on this.
//
// Keyboard access survives because a keydown arms it too: a keyboard user
// reaches the nav by pressing Tab, which arms before focus ever lands.
const useNavArmed = () => {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    // Clear focus the browser handed us from the PREVIOUS page. Following a
    // link out of a dropdown was observed landing on the next document with
    // document.activeElement still on that link, which is enough to reopen the
    // menu on its own. Nobody has legitimately focused a nav item on a page
    // this instant old, so dropping it costs nothing -- and a keyboard user's
    // first Tab lands wherever it would have anyway.
    const carriedFocus = document.activeElement;
    if (carriedFocus && carriedFocus.closest && carriedFocus.closest('nav')) {
      carriedFocus.blur();
    }

    // A touch device never sends pointermove before a tap, and its "hover" is
    // synthesised from the tap itself -- arming on the first touch keeps those
    // menus working.
    const arm = () => setArmed(true);

    window.addEventListener('pointermove', arm, { once: true });
    window.addEventListener('touchstart', arm, { once: true });
    window.addEventListener('keydown', arm, { once: true });

    return () => {
      window.removeEventListener('pointermove', arm);
      window.removeEventListener('touchstart', arm);
      window.removeEventListener('keydown', arm);
    };
  }, []);

  return armed;
};

export default useNavArmed;
